import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Info } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import { API, track } from "../lib/api";
import { useSettings } from "../lib/hooks";

const TYPES = [
  { value: "single-storey-extension", label: "Single-Storey Extension", unit: [1900, 2500], min: 35000, area: [15, 60], def: 30, note: "Rear or side extension, one level" },
  { value: "double-storey-extension", label: "Double-Storey Extension", unit: [1700, 2300], min: 55000, area: [24, 90], def: 45, note: "Two levels — best value per m²" },
  { value: "wrap-around-extension", label: "Wrap-Around Extension", unit: [2000, 2700], min: 60000, area: [20, 70], def: 35, note: "Rear and side combined" },
  { value: "loft-conversion", label: "Loft Conversion", unit: [1500, 2100], min: 40000, area: [15, 50], def: 28, note: "Dormer or rooflight conversion" },
  { value: "garage-conversion", label: "Garage Conversion", unit: [900, 1400], min: 15000, area: [12, 30], def: 18, note: "Integral or attached garage" },
  { value: "full-renovation", label: "Full Home Renovation", unit: [1000, 1800], min: 45000, area: [60, 300], def: 120, note: "Whole-house refurbishment" },
];

const SPECS = [
  { value: "essential", label: "Essential", mult: 0.9, note: "Quality standard finish" },
  { value: "premium", label: "Premium", mult: 1.0, note: "Our most popular specification" },
  { value: "signature", label: "Signature", mult: 1.25, note: "High-end materials and detailing" },
];

const AREAS = ["Bristol", "South Gloucestershire", "Bath & Around", "North Somerset", "Somerset", "South Wales", "Devon"];

const ADDONS = [
  { value: "kitchen", label: "New kitchen", range: [15000, 30000] },
  { value: "bathroom", label: "New bathroom", range: [8000, 15000] },
  { value: "glazing", label: "Extensive glazing / sliding doors", range: [5000, 12000] },
];

const FAQS = [
  { q: "How accurate is this estimate?", a: "It is an indicative range based on typical 2026 build costs across the South West. Ground conditions, structure, access, specification and design choices all move the final figure. Every NOOR project receives a detailed, itemised proposal after a site visit — that is the number you can rely on." },
  { q: "What does the estimate include?", a: "Indicative construction cost including VAT, covering structure, envelope, services and internal finish to the selected specification. It excludes planning or Building Regulations fees and loose furniture." },
  { q: "Do you provide a fixed quotation?", a: "We provide a detailed, itemised proposal before work begins, so you understand exactly what is included. Any changes during the project are agreed in writing before they are carried out." },
];

const gbp = (n) => "£" + Math.round(n / 1000).toLocaleString("en-GB") + "k";

export default function Calculator() {
  const { data: settings } = useSettings();
  const [type, setType] = useState(TYPES[0].value);
  const [area, setArea] = useState(TYPES[0].def);
  const [spec, setSpec] = useState("premium");
  const [location, setLocation] = useState(AREAS[0]);
  const [addons, setAddons] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const t = TYPES.find((x) => x.value === type);
  const s = SPECS.find((x) => x.value === spec);

  const estimate = useMemo(() => {
    let lo = t.unit[0] * area * s.mult;
    let hi = t.unit[1] * area * s.mult;
    ADDONS.filter((a) => addons.includes(a.value)).forEach((a) => {
      lo += a.range[0] * (s.mult * 0.5 + 0.5);
      hi += a.range[1] * s.mult;
    });
    const floor = t.min * s.mult;
    if (lo < floor) { lo = floor; hi = Math.max(hi, floor * 1.5); }
    return [Math.round(lo / 1000) * 1000, Math.round(hi / 1000) * 1000];
  }, [t, area, s, addons]);

  const summary = `Cost calculator estimate: ${t.label}, approx. ${area}m², ${s.label} specification, ${location}${addons.length ? `, add-ons: ${addons.join(", ")}` : ""}. Indicative range shown: ${gbp(estimate[0])} – ${gbp(estimate[1])}.`;

  const selectType = (v) => {
    const nt = TYPES.find((x) => x.value === v);
    setType(v);
    setArea(nt.def);
    track("calculator_type", { type: v });
  };

  const toggleAddon = (v) =>
    setAddons((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("Please confirm you are happy for us to contact you."); return; }
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("project_type", `${t.label} (cost calculator)`);
      fd.append("description", summary);
      fd.append("stage", "Early research");
      fd.append("consent", "true");
      fd.append("company", "");
      await API.post("/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      track("cost_calculator_complete", { project_type: type });
      setSent(true);
    } catch {
      setError("Something went wrong sending your details. Please try again or call us on 07782 760 279.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="calculator-page">
      <Seo
        title="Extension & Renovation Cost Calculator | NOOR"
        description="Estimate your extension, loft conversion or renovation cost in Bristol, Bath, Somerset and the surrounding areas — honest indicative ranges from NOOR."
        path="/cost-calculator"
        schema={{ "@context": "https://schema.org", "@graph": [
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Cost Calculator", path: "/cost-calculator" }]),
          faqSchema(FAQS),
        ] }}
      />

      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <Overline light>[ Cost Calculator ]</Overline>
              <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
                What might your project <span className="serif-accent text-ember text-[1.1em]">cost?</span>
              </h1>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
                Answer four quick questions for an honest, indicative range based on typical 2026 build costs across
                our region. Every NOOR project then receives a detailed, itemised proposal after a site visit — the
                number you can actually rely on.
              </p>
            </div>
            <div className="border border-white/15 p-2">
              <FrameReveal src="/api/uploads/brand/hero-kitchen.webp" alt="A bright hand-painted shaker kitchen with island and garden views" className="aspect-[4/3]" hoverZoom={false} eager testid="calc-hero-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          {/* ---------------- Inputs ---------------- */}
          <div className="lg:col-span-7 space-y-12">
            <Reveal>
              <Overline>[ Four Quick Questions ]</Overline>
              <h2 className="font-display font-light text-3xl tracking-tight mt-3">Build your estimate</h2>
            </Reveal>
            <Reveal>
              <p className="font-mono text-xs text-ember mb-5">[ 01 — Project Type ]</p>
              <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
                {TYPES.map((x) => (
                  <button
                    key={x.value}
                    type="button"
                    onClick={() => selectType(x.value)}
                    data-testid={`calc-type-${x.value}`}
                    className={`text-left p-6 transition-colors duration-300 ${type === x.value ? "bg-graphite text-white" : "bg-cream hover:bg-stone"}`}
                  >
                    <p className="font-display font-medium text-base md:text-lg">{x.label}</p>
                    <p className={`text-xs mt-1.5 ${type === x.value ? "text-white/55" : "text-graphite/50"}`}>{x.note}</p>
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs text-ember mb-5">[ 02 — Approximate Size ]</p>
              <div className="border border-black/10 p-6 md:p-8">
                <div className="flex items-baseline justify-between mb-6">
                  <span className="text-sm text-graphite/60">New floor area</span>
                  <span className="font-display font-medium text-2xl" data-testid="calc-area-value">{area} m²</span>
                </div>
                <input
                  type="range"
                  min={t.area[0]}
                  max={t.area[1]}
                  value={area}
                  onChange={(e) => setArea(Number(e.target.value))}
                  data-testid="calc-area-slider"
                  className="w-full accent-[#D95601]"
                />
                <div className="flex justify-between font-mono text-[10px] tracking-[0.15em] uppercase text-graphite/40 mt-3">
                  <span>{t.area[0]} m²</span>
                  <span>{t.area[1]} m²</span>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs text-ember mb-5">[ 03 — Specification ]</p>
              <div className="grid sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {SPECS.map((x) => (
                  <button
                    key={x.value}
                    type="button"
                    onClick={() => setSpec(x.value)}
                    data-testid={`calc-spec-${x.value}`}
                    className={`text-left p-6 transition-colors duration-300 ${spec === x.value ? "bg-graphite text-white" : "bg-cream hover:bg-stone"}`}
                  >
                    <p className="font-display font-medium text-base md:text-lg">{x.label}</p>
                    <p className={`text-xs mt-1.5 ${spec === x.value ? "text-white/55" : "text-graphite/50"}`}>{x.note}</p>
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs text-ember mb-5">[ 04 — Location & Extras ]</p>
              <div className="border border-black/10 p-6 md:p-8 space-y-6">
                <div>
                  <label className="text-sm text-graphite/60 block mb-2" htmlFor="calc-location">Project location</label>
                  <select
                    id="calc-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    data-testid="calc-location-select"
                    className="w-full border border-black/15 bg-white px-4 py-3 text-sm focus:outline-none focus:border-ember"
                  >
                    {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="space-y-3">
                  {ADDONS.map((a) => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => toggleAddon(a.value)}
                      data-testid={`calc-addon-${a.value}`}
                      className={`w-full flex items-center justify-between border px-5 py-4 text-sm transition-colors duration-300 ${addons.includes(a.value) ? "border-ember bg-ember/5" : "border-black/15 hover:border-black/40"}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`w-4 h-4 border flex items-center justify-center ${addons.includes(a.value) ? "border-ember bg-ember" : "border-black/30"}`}>
                          {addons.includes(a.value) && <Check size={11} className="text-white" />}
                        </span>
                        {a.label}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-graphite/40">
                        +{gbp(a.range[0])}–{gbp(a.range[1])}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* ---------------- Estimate panel ---------------- */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 bg-graphite text-white border border-white/10 p-8 md:p-10" data-testid="calc-estimate-panel">
              <p className="font-mono text-xs text-ember">[ Your Indicative Range ]</p>
              <p className="font-display font-light text-4xl md:text-5xl tracking-tighter mt-5" data-testid="calc-estimate-range">
                {gbp(estimate[0])} <span className="text-white/40">–</span> {gbp(estimate[1])}
              </p>
              <p className="text-white/55 text-sm leading-relaxed mt-4">
                {t.label} · approx. {area} m² · {s.label} specification · {location}
              </p>
              <div className="border-t border-white/10 mt-7 pt-6 space-y-2.5 text-sm text-white/60">
                <p className="flex gap-2.5"><Info size={14} className="text-ember shrink-0 mt-0.5" /> Indicative construction cost including VAT</p>
                <p className="flex gap-2.5"><Info size={14} className="text-ember shrink-0 mt-0.5" /> Structure, envelope, services and internal finish</p>
                <p className="flex gap-2.5"><Info size={14} className="text-ember shrink-0 mt-0.5" /> Final pricing follows a site visit and detailed proposal</p>
              </div>

              {sent ? (
                <div className="mt-8 border border-ember/50 bg-ember/10 p-6" data-testid="calc-success">
                  <p className="font-display font-medium text-lg">Thank you — we have your estimate.</p>
                  <p className="text-white/60 text-sm leading-relaxed mt-2">
                    We will be in touch to talk it through properly. Nothing is agreed at this stage — the next
                    step is simply a conversation.
                  </p>
                  <a
                    href={`https://wa.me/44${(settings?.whatsapp || "07782760279").replace(/^0/, "")}?text=${encodeURIComponent(`Hello NOOR. I just used your cost calculator. ${summary}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="calc-whatsapp-success"
                    className="mt-5 inline-flex items-center gap-2.5 text-sm font-semibold text-white/85 hover:text-ember transition-colors duration-300"
                  >
                    Want a quicker chat? Message this estimate on WhatsApp <ArrowRight size={14} />
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="calc-lead-form">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">
                    Discuss this estimate with us
                  </p>
                  <input
                    required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name" data-testid="calc-name-input"
                    className="w-full bg-transparent border border-white/20 focus:border-ember px-4 py-3 text-sm placeholder:text-white/35 focus:outline-none transition-colors"
                  />
                  <input
                    required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address" data-testid="calc-email-input"
                    className="w-full bg-transparent border border-white/20 focus:border-ember px-4 py-3 text-sm placeholder:text-white/35 focus:outline-none transition-colors"
                  />
                  <input
                    required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Telephone" data-testid="calc-phone-input"
                    className="w-full bg-transparent border border-white/20 focus:border-ember px-4 py-3 text-sm placeholder:text-white/35 focus:outline-none transition-colors"
                  />
                  <label className="flex items-start gap-3 text-xs text-white/55 leading-relaxed cursor-pointer">
                    <input
                      type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      data-testid="calc-consent-checkbox" className="mt-0.5 accent-[#D95601]"
                    />
                    <span>I am happy for NOOR Design &amp; Build to contact me about my project, as described in the <Link to="/privacy-policy" className="underline hover:text-white">privacy policy</Link>.</span>
                  </label>
                  {error && <p className="text-ember text-xs" data-testid="calc-error">{error}</p>}
                  <button
                    type="submit" disabled={sending} data-testid="calc-submit-button"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberdark disabled:opacity-60 text-white font-semibold tracking-wide px-8 py-4 text-sm transition-colors duration-300"
                  >
                    {sending ? "Sending…" : "Send my estimate to NOOR"}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <a
                    href={`https://wa.me/44${(settings?.whatsapp || "07782760279").replace(/^0/, "")}?text=${encodeURIComponent(`Hello NOOR. I'm using your cost calculator. ${summary}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="calc-whatsapp-send"
                    className="w-full inline-flex items-center justify-center gap-3 border border-white/25 hover:border-[#25D366] text-white/80 hover:text-[#25D366] font-semibold tracking-wide px-8 py-4 text-sm transition-colors duration-300"
                  >
                    Or send it instantly on WhatsApp
                  </a>
                </form>
              )}

              <p className="text-white/35 text-xs leading-relaxed mt-6">
                Prefer the full questionnaire? <Link to="/request-a-consultation" className="underline hover:text-white" data-testid="calc-full-form-link">Request a consultation</Link> instead.
              </p>
            </div>
          </div>
        </div>

        {/* ---------------- FAQs ---------------- */}
        <div className="max-w-[900px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
          <Reveal>
            <Overline>[ Honest Answers ]</Overline>
            <div className="mt-8 space-y-px">
              {FAQS.map((f, i) => (
                <details key={i} className="border border-black/10 bg-cream group" data-testid={`calc-faq-${i}`}>
                  <summary className="cursor-pointer p-6 font-display font-medium text-base md:text-lg hover:text-ember transition-colors duration-300 list-none flex items-center justify-between">
                    {f.q}
                    <span className="font-mono text-ember text-lg group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <p className="px-6 pb-6 text-sm text-graphite/60 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
